import { useState } from "react";
import { api, ApiError } from "../../lib/api.js";
import { useT } from "../../i18n/index.js";

interface PinEntryProps {
  initialPin?: string;
  onValidated: (pin: string, quizTitle: string, twoStepRequired: boolean) => void;
}

export function PinEntry({ initialPin = "", onValidated }: PinEntryProps) {
  const t = useT();
  const [pin, setPin] = useState(initialPin);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = pin.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setError(t("join.codeRequired"));
      return;
    }
    setError("");
    setLoading(true);
    try {
      const data = await api.get<{ quizTitle: string; twoStepRequired?: boolean }>(`/join/${trimmed}`);
      onValidated(trimmed, data.quizTitle, data.twoStepRequired ?? false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setError(t("join.gameNotFound"));
      } else {
        setError(t("error.generic"));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <form className="join-form" onSubmit={handleSubmit} aria-label={t("join.formLabel")}>
      <label className="join-field" htmlFor="pin-input">
        <span>{t("join.enterCode")}</span>
        <input
          id="pin-input"
          className="join-pin-input"
          type="text"
          inputMode="numeric"
          maxLength={6}
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
          placeholder="000000"
          autoFocus
          aria-label={t("join.codeAriaLabel")}
          aria-describedby={error ? "pin-error" : undefined}
        />
      </label>
      {error && (
        <p id="pin-error" role="alert" className="join-error">
          {error}
        </p>
      )}
      <button className="join-primary-button" type="submit" disabled={loading}>
        {loading ? t("join.checking") : t("join.joinGame")}
      </button>
    </form>
  );
}
